///TODO MOVE TO NPM PACKAGE

import { TZDate } from "@date-fns/tz";
import {
  sub,
  add,
  startOfDay,
  startOfWeek,
  startOfMonth,
  startOfQuarter,
  startOfYear,
  endOfDay,
  endOfWeek,
  endOfMonth,
  endOfQuarter,
  endOfYear,
} from "date-fns";

//holds the current date in the correct timezone, made global so it can be used in the date range calculations more easily
class Today {
  private static today: TZDate;

  public static init(timezone: string) {
    const todayInTimezone = new TZDate(Date.now(), timezone);
    //make sure we have a valid timezone
    if (isNaN(todayInTimezone.getTime())) {
      throw new Error(`Invalid timezone: ${timezone}`);
    }
    Today.today = new TZDate(Date.now(), timezone);
  }

  public static get() {
    return Today.today;
  }
}

/**Determines the date math that needs to get done*/
type DateUnit = "DAY" | "WEEK" | "MONTH" | "QUARTER" | "YEAR" | "WEEKDAY" | "WORKDAY";
/**
 * Determines the range of dates to be used
 * LAST: Start at 12:00:00 AM of the first day of n date units before the current day and ends at 11:59:59 PM of the last day of the date unit before the current date unit
 * AGO: Start at 12:00:00 AM of the first day of n date units before the current day and ends at 11:59:59 PM of the last of the current date unit
 * THIS: Start at 12:00:00 AM of the first day of the current date unit and ends at 11:59:59 PM of the last day of the current date unit
 * NEXT: Start at 12:00:00 AM of the first day of the next date unit and ends at 11:59:59 PM of the last day of n date units after the current date unit
 * AHEAD: Start at 12:00:00 AM of n date units after the current date unit and ends at 11:59:59 PM of the last day of n date units after the current date unit
 */
type DateRange = "LAST" | "AGO" | "THIS" | "NEXT" | "AHEAD";
/**
 * Modifies the date range set by the date unit and date range
 * AFTER: Start at the end of the date range and till the end of time (if a nested date range is used, this is overridden)
 * SINCE: Start at the start of the date range and till the end of time (if a nested date range is used, this is overridden)
 * THROUGH: Start at the beginning of time and end at the end of the date range, can also be used to create a nested date range, in which case the end of the nested date range is the end of the date range
 * UP-TO: Start at the beginning of time and end at the start of the date range, can also be used to create a nested date range, in which case the start of the nested date range is the start of the date range
 */
type DateRangeModifier = "AFTER" | "THROUGH" | "UP-TO" | "SINCE";

/**
 * Created a nested date range uses recursion, this constant determines the maximum depth of recursion
 */
const MAX_RECURSION_DEPTH = 1;

/**
 * Allows for a look up of what function to use from the date-fns library to add or subtract date units, if the date range is not present, a custom function needs to be created
 */
const DateFunctionLookup = {
  LAST: sub,
  AGO: sub,
  NEXT: add,
  AHEAD: add,
} as const;

/**
 * Allows for a look up of what duration to use when adding or subtracting date units using the date-fns library, if the date range is not present, a custom function needs to be created
 */
const DateDurationLookup = {
  DAY: "days",
  WEEK: "weeks",
  MONTH: "months",
  YEAR: "years",
} as const;

/**
 * Allows for a look up of what function to use from the date-fns library to get the start of a date unit
 */
const StartDateFunctionLookup = {
  DAY: startOfDay,
  WORKDAY: startOfDay,
  WEEKDAY: startOfDay,
  WEEK: startOfWeek,
  MONTH: startOfMonth,
  QUARTER: startOfQuarter,
  YEAR: startOfYear,
} as const;

/**
 * Allows for a look up of what function to use from the date-fns library to get the end of a date unit
 */
const EndDateFunctionLookup = {
  DAY: endOfDay,
  WORKDAY: endOfDay,
  WEEKDAY: endOfDay,
  WEEK: endOfWeek,
  MONTH: endOfMonth,
  QUARTER: endOfQuarter,
  YEAR: endOfYear,
} as const;

/**
 * converts a number or string to a fiscal month index, where 0 is January and 11 is December
 * if undefined is returned, the input is invalid
 */
const FiscalMonthIndexLookup: Record<string | number, 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | undefined> = {
  1: 0,
  2: 1,
  3: 2,
  4: 3,
  5: 4,
  6: 5,
  7: 6,
  8: 7,
  9: 8,
  10: 9,
  11: 10,
  12: 11,
  JANUARY: 0,
  FEBRUARY: 1,
  MARCH: 2,
  APRIL: 3,
  MAY: 4,
  JUNE: 5,
  JULY: 6,
  AUGUST: 7,
  SEPTEMBER: 8,
  OCTOBER: 9,
  NOVEMBER: 10,
  DECEMBER: 11,
};

/**
 * converts a string to a number, if undefined is returned, the input is invalid
 * allows for strings to contain written numbers up to twenty
 */
const DateNumberLookUp: Record<string, number | undefined> = {
  ONE: 1,
  TWO: 2,
  THREE: 3,
  FOUR: 4,
  FIVE: 5,
  SIX: 6,
  SEVEN: 7,
  EIGHT: 8,
  NINE: 9,
  TEN: 10,
  ELEVEN: 11,
  TWELVE: 12,
  THIRTEEN: 13,
  FOURTEEN: 14,
  FIFTEEN: 15,
  SIXTEEN: 16,
  SEVENTEEN: 17,
  EIGHTEEN: 18,
  NINETEEN: 19,
  TWENTY: 20,
};

/**
 * takes the index of the current day of the week and returns the offset needed to add to the number of days to get the correct number of weekends that need to be added
 * if undefined is returned, the input is invalid
 */
const WeekdayAddOffsetConversion: Record<number, -1 | 0 | 1 | 2 | 3 | 4 | undefined> = {
  0: -1,
  1: 0,
  2: 1,
  3: 2,
  4: 3,
  5: 4,
  6: -1,
};

/**
 * takes the index of the current day of the week and returns the offset needed to add to the number of days to get the correct number of weekends that need to be added
 * if undefined is returned, the input is invalid
 */
const WeekdaySubOffsetConversion: Record<number, -1 | 0 | 1 | 2 | 3 | 4 | undefined> = {
  0: -1,
  1: 4,
  2: 3,
  3: 2,
  4: 1,
  5: 0,
  6: -1,
};

/**
 * replaces common date filter string parts with the correct string, use before parsing but after converting to uppercase
 */
const ReplaceMap: Record<string, string> = {
  "WORK DAYS": "WORKDAY",
  "WORK DAY": "WORKDAY",
  "WEEK DAYS": "WEEKDAY",
  "WEEK DAY": "WEEKDAY",
  "TODATE": "TO-DATE",
  "TO DATE": "TO-DATE",
  "FISCALYEAR": "FISCAL YEAR",
  "FISCALQUARTER": "FISCAL QUARTER",
  "UP TO": "UP-TO",
  "UPTO": "UP-TO",
};

/**
 * Function to get the offset needed for weekend days in weekday date calculations
 * @param dayOfWeek index of the current day of the week 0 is Sunday, 6 is Saturday
 * @param isPreviousDateRange determines if the reference date is before or after the current date
 * @returns offset used to handle weekend edge cases
 */
function getWeekendOffset(dayOfWeek: number, isPreviousDateRange: boolean): number {
  if (dayOfWeek === 0 || dayOfWeek === 6) {
    if (isPreviousDateRange) {
      return dayOfWeek === 0 ? 1 : 0;
    }
    return dayOfWeek === 0 ? 0 : 1;
  }
  return 0;
}

/**
 * Calculates the number of days needed to get the correct weekday date
 * Checks the number of weekdays to add or subtract and adjusts for weekends
 * @param date starting date, usually the current date
 * @param dateRange enum that determines the range of dates to be used, cannot be THIS
 * @param dateNum number of weekdays to add or subtract
 * @returns number of days to add or subtract to get the correct weekday date
 */
function calculateWeekdayDateNumber(date: Date, dateRange: "LAST" | "AGO" | "NEXT" | "AHEAD", dateNum: number): number {
  const dayOfWeek = date.getDay();
  const isPreviousDateRange = dateRange === "LAST" || dateRange === "AGO";
  const weekdayOffset = isPreviousDateRange
    ? WeekdaySubOffsetConversion[dayOfWeek]
    : WeekdayAddOffsetConversion[dayOfWeek];
  if (weekdayOffset === undefined) throw new Error("Invalid day of the week");
  const weekendOffset = getWeekendOffset(dayOfWeek, isPreviousDateRange);
  const newDateNum = dateNum + Math.floor((dateNum + weekdayOffset) / 5) * 2;
  return newDateNum + weekendOffset;
}

/**
 * Calculates the number of days needed to get the correct workday date
 * Checks the number of days off that fall within the date range and adds them to the number of days to add or subtract
 * @param date starting date, usually the current date
 * @param referenceDate initial reference date before days off are added
 * @param daysOff array of dates that are days off
 * @returns number of days to add or subtract to the current date number to get the correct workday date
 */
function calculateWorkDayDateNumberOffset(date: Date, referenceDate: Date, daysOff: Date[]): number {
  const isPreviousDateRange = referenceDate < date;
  const dayOffInRangeCheck = (date: Date, referenceDate: Date) => (dayOff: Date) => {
    if (isPreviousDateRange) {
      return dayOff <= date && dayOff >= referenceDate;
    }
    return dayOff >= date && dayOff <= referenceDate;
  };
  const daysOffInRange = daysOff.filter(dayOffInRangeCheck(date, referenceDate));
  //base case
  const daysOffInRangeCount = daysOffInRange.length;
  if (!daysOffInRangeCount) {
    return 0;
  }
  const newReferenceDate = isPreviousDateRange
    ? sub(referenceDate, { days: daysOffInRangeCount })
    : add(referenceDate, { days: daysOffInRangeCount });
  const newDaysOff = daysOff.filter((dayOff) => !daysOffInRange.includes(dayOff));
  return daysOffInRangeCount + calculateWorkDayDateNumberOffset(date, newReferenceDate, newDaysOff);
}

/**
 * Calculates the reference date to base the start and end date on
 * Takes in the date unit, date range, number of date units to add or subtract, and an array of days off
 * @param dateUnit enum that determines the date unit to be used
 * @param dateRange enum that determines the range of dates to be used
 * @param dateNum number of date units to add or subtract
 * @param daysOff array of dates that are days off, only used for workday date units
 * @returns
 */
function getReferenceDate(dateUnit: DateUnit, dateRange: DateRange, dateNum: number, daysOff: Date[]) {
  const today = Today.get();
  // if the date range is THIS, return the current date
  if (dateRange === "THIS") {
    return today;
  }
  // if no date number is provided, default to 1
  const dateNumber = dateNum || 1;
  // get the function to use to add or subtract date units
  const dateFunction = DateFunctionLookup[dateRange];
  if (!dateFunction) throw new Error("Invalid date range");
  // date-fns does not have a function to add or subtract quarters, so a custom function is needed
  if (dateUnit === "QUARTER") {
    return dateFunction(today, { months: dateNumber * 3 });
  }
  // date-fns does not have a function to add or subtract weekdays, so a custom function is needed
  if (dateUnit === "WEEKDAY") {
    return dateFunction(today, { days: calculateWeekdayDateNumber(today, dateRange, dateNumber) });
  }
  // workdays require an array of days off to be provided, if none are provided, the function will default to the custom weekday function
  if (dateUnit === "WORKDAY") {
    if (!daysOff.length) {
      return dateFunction(today, { days: calculateWeekdayDateNumber(today, dateRange, dateNumber) });
    }
    const referenceDateBeforeDaysOff = dateFunction(today, { days: dateNumber });
    return dateFunction(today, {
      days: calculateWorkDayDateNumberOffset(today, referenceDateBeforeDaysOff, daysOff) + dateNumber,
    });
  }
  // all other date units can use the date-fns functions
  const duration = DateDurationLookup[dateUnit];
  if (!duration) throw new Error("Invalid date unit");
  return dateFunction(today, { [duration]: dateNumber });
}

/**
 * Gets the start date of the date range
 * @param referenceDate reference date based on the date unit, date range, and number of date units
 * @param dateUnit enum that determines the date unit to be used
 * @param dateRange enum that determines the range of dates to be used
 * @param daysOff array of dates that are days off, only used for workday date units
 * @param toDateSet boolean that determines if the date range is set to the current date, only used for AGO date ranges
 * @returns Start date of the date range
 */
function getStartDate(
  referenceDate: Date,
  dateUnit: DateUnit,
  dateRange: DateRange,
  daysOff: Date[],
  toDateSet: boolean,
) {
  const startDateFunction = StartDateFunctionLookup[dateUnit];
  if (!startDateFunction) throw new Error("Invalid date unit");
  //AGO date ranges should start at the reference date if the to-date is set
  if (toDateSet && dateRange === "AGO") {
    return referenceDate;
  }
  //NEXT date ranges should start at the start of the next date unit
  if (dateRange === "NEXT") {
    return startDateFunction(getReferenceDate(dateUnit, dateRange, 1, daysOff));
  }
  return startDateFunction(referenceDate);
}

/**
 * Gets the end date of the date range
 * @param referenceDate reference date based on the date unit, date range, and number of date units
 * @param dateUnit enum that determines the date unit to be used
 * @param dateRange enum that determines the range of dates to be used
 * @param daysOff array of dates that are days off, only used for workday date units
 * @returns End date of the date range
 */
function getEndDate(referenceDate: Date, dateUnit: DateUnit, dateRange: DateRange, daysOff: Date[]) {
  const endDateFunction = EndDateFunctionLookup[dateUnit];
  if (!endDateFunction) throw new Error("Invalid date unit");
  //LAST date ranges should end at the end of the previous date unit
  if (dateRange === "LAST") {
    return endDateFunction(getReferenceDate(dateUnit, dateRange, 1, daysOff));
  }
  return endDateFunction(referenceDate);
}

/**
 * Gets the start date of the fiscal date range
 * @param startDate current start date of the date range before the fiscal date range is applied
 * @param dateUnit enum that determines the date unit to be used, only YEAR and QUARTER are valid
 * @param dateRange enum that determines the range of dates to be used
 * @param fiscalMonthIndex index of the fiscal month, where 0 is January and 11 is December
 * @returns Start date of the fiscal date range
 */
function fiscalStartDate(
  startDate: Date,
  dateUnit: "YEAR" | "QUARTER",
  dateRange: DateRange,
  fiscalMonthIndex: number,
) {
  switch (dateUnit) {
    case "YEAR":
      //add the number of months to the start date to get the fiscal start date
      const fiscalStartDate = add(startDate, { months: fiscalMonthIndex });
      //if the fiscal month is after the current month, we are in the next fiscal year and need to subtract a year
      if (fiscalMonthIndex > Today.get().getMonth()) {
        return sub(fiscalStartDate, { years: 1 });
      }
      return fiscalStartDate;
    case "QUARTER":
      //get the offset needed to get the correct fiscal quarter
      const monthOffset = fiscalMonthIndex % 3;
      if (dateRange === "THIS") {
        const thisMonthOffset = monthOffset === 0 ? 0 : 3 - monthOffset;
        return startOfMonth(sub(startDate, { months: thisMonthOffset }));
      }
      const fiscalQuarterStartDate = startOfMonth(add(startDate, { months: monthOffset }));
      const fiscalQuarterEndDate = endOfMonth(add(fiscalQuarterStartDate, { months: 2 }));
      const today = Today.get();
      //check if the current date falls within the fiscal quarter we just calculated
      if (today >= fiscalQuarterStartDate && today <= fiscalQuarterEndDate) {
        switch (dateRange) {
          case "LAST":
            return startOfMonth(sub(fiscalQuarterStartDate, { months: 3 }));
          case "AGO":
            return startOfMonth(sub(fiscalQuarterStartDate, { months: 3 }));
          case "NEXT":
            return startOfMonth(add(fiscalQuarterStartDate, { months: 3 }));
          case "AHEAD":
            return startOfMonth(add(fiscalQuarterStartDate, { months: 3 }));
        }
      }
      return fiscalQuarterStartDate;
    default:
      throw new Error("Invalid date unit for fiscal start date");
  }
}

/**
 * Gets the end date of the fiscal date range
 * @param endDate current end date of the date range before the fiscal date range is applied
 * @param dateUnit enum that determines the date unit to be used, only YEAR and QUARTER are valid
 * @param dateRange enum that determines the range of dates to be used
 * @param fiscalMonthIndex index of the fiscal month, where 0 is January and 11 is December
 * @returns End date of the fiscal date range
 */
function fiscalEndDate(endDate: Date, dateUnit: "YEAR" | "QUARTER", dateRange: DateRange, fiscalMonthIndex: number) {
  switch (dateUnit) {
    case "YEAR":
      //add the number of months to the start date to get the fiscal start date
      const fiscalEndDate = add(endDate, { months: fiscalMonthIndex });
      if (fiscalMonthIndex > Today.get().getMonth()) {
        return sub(fiscalEndDate, { years: 1 });
      }
      //if the fiscal month is after the current month, we are in the next fiscal year and need to subtract a year
      return fiscalEndDate;
    case "QUARTER":
      //get the offset needed to get the correct fiscal quarter
      const monthOffset = fiscalMonthIndex % 3;
      if (dateRange === "THIS") {
        const thisMonthOffset = monthOffset === 0 ? 0 : 3 - monthOffset;
        return endOfMonth(sub(endDate, { months: thisMonthOffset }));
      }
      const fiscalQuarterEndDate = endOfMonth(add(endDate, { months: monthOffset }));
      const fiscalQuarterStartDate = startOfMonth(sub(fiscalQuarterEndDate, { months: 2 }));
      const today = Today.get();
      //check if the current date falls within the fiscal quarter we just calculated
      if (today >= fiscalQuarterStartDate && today <= fiscalQuarterEndDate) {
        switch (dateRange) {
          case "LAST":
            return endOfMonth(sub(fiscalQuarterEndDate, { months: 3 }));
          case "AGO":
            return endOfMonth(sub(fiscalQuarterEndDate, { months: 3 }));
          case "NEXT":
            return endOfMonth(add(fiscalQuarterEndDate, { months: 3 }));
          case "AHEAD":
            return endOfMonth(add(fiscalQuarterEndDate, { months: 3 }));
        }
      }
      return fiscalQuarterEndDate;
    default:
      throw new Error("Invalid date unit for fiscal end date");
  }
}

/**
 * Takes the date range calculated uses the date range modifier to finalize the date range
 * @param dateRange date range calculated from the date unit, date range, and number of date units
 * @param dateRangeModifier enum that determines how the date range should be modified
 * @returns
 */
function finalizeDateRange(
  dateRange: {
    startDate?: Date;
    endDate?: Date;
  },
  dateRangeModifier: DateRangeModifier,
) {
  const { startDate, endDate } = dateRange;
  switch (dateRangeModifier) {
    //greater than
    case "AFTER":
      return {
        startDate: endDate || startDate || null,
        endDate: null,
      };
    //less than or equal to
    case "THROUGH":
      return {
        startDate: null,
        endDate: endDate || startDate || null,
      };
    //less than
    case "UP-TO":
      return {
        startDate: null,
        endDate: startDate || endDate || null,
      };
    //greater than or equal to
    case "SINCE":
      return {
        startDate: startDate || endDate || null,
        endDate: null,
      };
    default:
      throw new Error(`Invalid date range modifier: ${dateRangeModifier}`);
  }
}

/**
 * gets the date range based on the date filter string before any date range modifiers or nested date ranges are applied
 * @param dateUnit enum that determines the date unit to be used
 * @param dateRange enum that determines the range of dates to be used
 * @param dateNum number of date units to add or subtract
 * @param fiscalMonthIndex index of the month of the start of the fiscal year, where 0 is January and 11 is December, only used for fiscal date ranges
 * @param daysOff array of dates that are days off, only used for workday date units
 * @param toDateSet boolean that determines if the date range is set to the current date, only used for AGO date ranges
 * @returns start and end date of the date range
 */
function getDateRange(
  dateUnit: DateUnit,
  dateRange: DateRange,
  dateNum: number,
  fiscalMonthIndex: 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11,
  daysOff: Date[],
  toDateSet: boolean,
) {
  const referenceDate = getReferenceDate(dateUnit, dateRange, dateNum, daysOff);
  const startDate = getStartDate(referenceDate, dateUnit, dateRange, daysOff, toDateSet);
  const endDate = getEndDate(referenceDate, dateUnit, dateRange, daysOff);
  //TODO: add fiscal quarter support
  if (fiscalMonthIndex && dateUnit === "YEAR") {
    return {
      startDate: fiscalStartDate(startDate, dateUnit, dateRange, fiscalMonthIndex),
      endDate: fiscalEndDate(endDate, dateUnit, dateRange, fiscalMonthIndex),
    };
  }
  return {
    startDate,
    endDate,
  };
}

/**
 * gets the date range modifier from the date filter string
 * @param dateFilterStringPart string broken down from the date filter string
 * @returns either a valid date range modifier or undefined
 */
function getDateRangeModifier(dateFilterStringPart: string) {
  if (dateFilterStringPart === "TO") {
    return "THROUGH";
  }
  if (
    dateFilterStringPart === "AFTER" ||
    dateFilterStringPart === "THROUGH" ||
    dateFilterStringPart === "UP-TO" ||
    dateFilterStringPart === "SINCE"
  ) {
    return dateFilterStringPart as DateRangeModifier;
  }
  return undefined;
}

/**
 * gets the date unit from the date filter string
 * @param dateFilterStringPart string broken down from the date filter string
 * @returns either a valid date unit or undefined
 */
function getDateUnit(dateFilterStringPart: string) {
  const singularizedDateUnit = dateFilterStringPart.endsWith("S")
    ? dateFilterStringPart.slice(0, -1)
    : dateFilterStringPart;
  if (
    singularizedDateUnit === "DAY" ||
    singularizedDateUnit === "WEEK" ||
    singularizedDateUnit === "MONTH" ||
    singularizedDateUnit === "QUARTER" ||
    singularizedDateUnit === "YEAR" ||
    singularizedDateUnit === "WEEKDAY" ||
    singularizedDateUnit === "WORKDAY"
  ) {
    return singularizedDateUnit as DateUnit;
  }
  return undefined;
}

/**
 * gets the date number from the date filter string, either parses the number or uses a look up
 * @param dateFilterStringPart string broken down from the date filter string
 * @returns a number or undefined
 */
function getDateNumber(dateFilterStringPart: string) {
  const parsedDateNum = parseInt(dateFilterStringPart);
  if (!isNaN(parsedDateNum)) {
    return parsedDateNum;
  }
  return DateNumberLookUp[dateFilterStringPart];
}

/**
 * gets the date range unit from the date filter string
 * @param dateFilterStringPart string broken down from the date filter string
 * @returns either a valid date range unit or undefined
 */
function getDateRangeUnit(dateFilterStringPart: string) {
  if (dateFilterStringPart === "PREVIOUS") {
    return "LAST";
  }
  if (
    dateFilterStringPart === "LAST" ||
    dateFilterStringPart === "AGO" ||
    dateFilterStringPart === "THIS" ||
    dateFilterStringPart === "NEXT" ||
    dateFilterStringPart === "AHEAD"
  ) {
    return dateFilterStringPart as DateRange;
  }
  return undefined;
}

/**
 * allows for special date units to be used in the date filter string that can define the unit, range, and number of date units
 * @param dateFilterStringPart string broken down from the date filter string
 * @returns an object with the date number, date range unit, and date unit or undefined
 */
function specialDayUnits(dateFilterStringPart: string):
  | {
      dateNumber?: number;
      dateRangeUnit: DateRange;
      dateUnit: DateUnit;
    }
  | undefined {
  switch (dateFilterStringPart) {
    case "YESTERDAY":
      return {
        dateNumber: 1,
        dateRangeUnit: "LAST",
        dateUnit: "DAY",
      };
    case "TODAY":
      return {
        dateRangeUnit: "THIS",
        dateUnit: "DAY",
      };
    case "TOMORROW":
      return {
        dateNumber: 1,
        dateRangeUnit: "NEXT",
        dateUnit: "DAY",
      };
    default:
      return undefined;
  }
}

/**
 * gets the fiscal month index from the date filter string, either parses the number or uses a look up
 * @param startOfFiscalYear string or number that represents the start of the fiscal year, 1-12 or the month name
 * @returns index of the fiscal month, where 0 is January and 11 is December, or undefined
 */
function getFiscalMonthIndex(
  startOfFiscalYear: string | number,
): 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | undefined {
  if (typeof startOfFiscalYear === "string") {
    const parsedFiscalMonth = parseInt(startOfFiscalYear);
    if (!isNaN(parsedFiscalMonth)) {
      const fiscalMonthIndex = FiscalMonthIndexLookup[parsedFiscalMonth];
      return fiscalMonthIndex;
    }
    const fiscalMonthIndex = FiscalMonthIndexLookup[startOfFiscalYear.toUpperCase()];
    return fiscalMonthIndex;
  }
  return FiscalMonthIndexLookup[startOfFiscalYear];
}

/**
 * Main function that takes a parsed date filter string broken up into parts and returns the start and end date of the date range
 * Uses recursion to handle nested date ranges
 * @param dateFilterParts array of strings that make up the date filter (should have been converted to uppercase and have common parts replaced)
 * @param fiscalMonthIndex index of the start of the fiscal year, where 0 is January and 11 is December
 * @param sortedDaysOff days off sorted in ascending order
 * @param recursionDepth current depth of recursion, used to prevent stack overflow
 * @returns
 */
function getDateRangeFromDateFilterParts(
  dateFilterParts: string[],
  fiscalMonthIndex: 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | undefined,
  sortedDaysOff: Date[],
  recursionDepth: number = 0,
): {
  startDate: Date | null;
  endDate: Date | null;
} {
  if (recursionDepth > MAX_RECURSION_DEPTH) {
    throw new Error("Too many nested date ranges");
  }
  let dateUnit: DateUnit | undefined;
  let dateRangeUnit: DateRange | undefined;
  let dateNumber: number | undefined;
  let dateRangeModifier: DateRangeModifier | undefined;
  let useFiscalDateRange = false;
  let toDateSet = false;
  let counter = 0;
  let nestedEndDate: Date | undefined = undefined;
  for (const part of dateFilterParts) {
    //date range modifiers are in the start of the filter, or start of a nested date range
    const dateRangeModifierResult = getDateRangeModifier(part);
    if (dateRangeModifierResult) {
      //if a date range modifier is set or we are not at the start of the filter, check to see if we can set a nested date range
      if (dateRangeModifier || counter) {
        //only allow nested date ranges to be set with THROUGH or UP-TO
        if (dateRangeModifierResult === "THROUGH" || dateRangeModifierResult === "UP-TO") {
          if (toDateSet) {
            throw new Error(`Cannot set nested date with to-date`);
          }
          if (nestedEndDate) {
            throw new Error(`Cannot have more than one nested date range`);
          }
          if (!dateUnit || !dateRangeUnit) {
            throw new Error(`Cannot set nested date range without and initial date range`);
          }
          //start nested filter from after the current date range modifier
          const { startDate, endDate } = getDateRangeFromDateFilterParts(
            dateFilterParts.slice(counter + 1),
            fiscalMonthIndex,
            sortedDaysOff,
            recursionDepth + 1,
          );
          //finalize the nested date range
          const { startDate: adjustedStartDate, endDate: adjustedEndDate } = finalizeDateRange(
            { startDate: startDate || undefined, endDate: endDate || undefined },
            dateRangeModifierResult,
          );
          //set the end date of the nested date range for the final date range
          nestedEndDate = adjustedEndDate || adjustedStartDate || undefined;
          break;
        }
        if (dateRangeModifier)
          throw new Error(`${dateRangeModifier} already set, cannot set ${dateRangeModifierResult}`);
        if (counter) throw new Error(`${dateRangeModifierResult} has to be at the start of the filter`);
      }
      dateRangeModifier = dateRangeModifierResult;
      counter++;
      continue;
    }

    //TOMORROW, TODAY, YESTERDAY
    const specialDayUnitsResult = specialDayUnits(part);
    if (specialDayUnitsResult) {
      if (dateUnit || dateRangeUnit || dateNumber) {
        throw new Error(`${part} cannot be combined with other date parts`);
      }
      dateUnit = specialDayUnitsResult.dateUnit;
      dateRangeUnit = specialDayUnitsResult.dateRangeUnit;
      dateNumber = specialDayUnitsResult.dateNumber;
      counter++;
      continue;
    }

    const dateRangeUnitResult = getDateRangeUnit(part);
    if (dateRangeUnitResult) {
      if (dateRangeUnit) {
        throw new Error(`${dateRangeUnit} already set, cannot set ${dateRangeUnitResult}`);
      }
      //THIS, NEXT, LAST go before the date number and unit
      if (dateRangeUnitResult === "THIS" || dateRangeUnitResult === "NEXT" || dateRangeUnitResult === "LAST") {
        if (useFiscalDateRange || dateUnit) {
          throw new Error(`${dateRangeUnitResult} is out of order`);
        }
      }
      //AHEAD, AGO go after the date unit
      if (dateRangeUnitResult === "AHEAD" || dateRangeUnitResult === "AGO") {
        if (!dateUnit) {
          throw new Error(`${dateRangeUnitResult} is out of order`);
        }
      }
      dateRangeUnit = dateRangeUnitResult;
      counter++;
      continue;
    }

    const dateNumberResult = getDateNumber(part);
    if (dateNumberResult) {
      if (dateUnit) {
        throw new Error(`${dateUnit} must be placed before date number`);
      }
      if (dateNumber) {
        throw new Error(`${dateNumber} already set, cannot set ${dateNumberResult}`);
      }
      if (dateRangeUnit === "THIS") {
        throw new Error(`Cannot set number for THIS date range`);
      }
      dateNumber = dateNumberResult;
      counter++;
      continue;
    }

    if (part === "FISCAL") {
      if (useFiscalDateRange) {
        throw new Error(`FISCAL included more than once`);
      }
      if (dateUnit) {
        throw new Error(`FISCAL must be placed before date unit`);
      }
      useFiscalDateRange = true;
      counter++;
      continue;
    }

    const dateUnitResult = getDateUnit(part);
    if (dateUnitResult) {
      if (dateUnit) {
        throw new Error(`${dateUnit} already set, cannot set ${dateUnitResult}`);
      }
      //TODO: add support for fiscal quarters
      if (useFiscalDateRange && dateUnitResult !== "YEAR") {
        throw new Error(`Fiscal date range can only be set for year`);
      }
      dateUnit = dateUnitResult;
      counter++;
      continue;
    }

    //To date is a special case that can only be set at the end of the filter, it sets the end date to the current date, prevents nested date ranges, and shifts the start of ago date ranges to the reference date
    if (part === "TO-DATE") {
      if (recursionDepth) {
        throw new Error(`To date cannot be set in a nested date range`);
      }
      if (counter > dateFilterParts.length - 1) {
        throw new Error(`To date must be at the end of the filter`);
      }
      if (toDateSet) {
        throw new Error(`To date already set`);
      }
      if (nestedEndDate) {
        throw new Error(`To date cannot be set with a nested date`);
      }
      toDateSet = true;
      counter++;
      continue;
    }
    throw new Error(`Invalid date filter part: ${part}`);
  }
  if (!dateUnit) {
    throw new Error(`Date unit not set`);
  }
  if (!dateRangeUnit) {
    throw new Error(`Date range unit not set`);
  }
  const initialDateRange = getDateRange(
    dateUnit,
    dateRangeUnit,
    dateNumber || 1,
    useFiscalDateRange ? fiscalMonthIndex || 0 : 0,
    sortedDaysOff,
    toDateSet,
  );
  const finalDateRange = !!dateRangeModifier
    ? finalizeDateRange(initialDateRange, dateRangeModifier)
    : initialDateRange;
  if (toDateSet) {
    if (finalDateRange.startDate && finalDateRange.startDate > Today.get()) {
      throw new Error(`Start date can't be after current date when setting to-date`);
    }
    return {
      startDate: finalDateRange.startDate || null,
      endDate: Today.get(),
    };
  }
  if (nestedEndDate && finalDateRange.startDate && nestedEndDate < finalDateRange.startDate) {
    throw new Error(`Nested date range end date cannot be before start date`);
  }
  return {
    startDate: finalDateRange.startDate || null,
    endDate: nestedEndDate || finalDateRange.endDate || null,
  };
}

/**
 * Takes in a date filter string and returns the start and end date of the date range
 * Breaks up a filter string into a date unit, date range, date number, date range modifier
 * @param dateFilterString string that represents the date filter
 * @param startOfFiscalYear string or number that represents the start of the fiscal year, 1-12 or the month name
 * @param daysOff array of dates that are days off
 * @returns
 */
export function parseDateFilterString(
  dateFilterString: string,
  startOfFiscalYear: string | number,
  daysOff: Date[] = [],
  timezone: string,
): {
  startDate: Date | null;
  endDate: Date | null;
} {
  Today.init(timezone);
  const fiscalMonthIndex = getFiscalMonthIndex(startOfFiscalYear);
  if (fiscalMonthIndex === undefined) {
    throw new Error(`Invalid start of fiscal year: ${startOfFiscalYear}`);
  }
  //ensure the days off are in the correct timezone and sort them
  const sortedDaysOff = daysOff
    .map((dayOff) => {
      return startOfDay(new TZDate(dayOff.getUTCFullYear(), dayOff.getUTCMonth(), dayOff.getUTCDate(), timezone));
    })
    .sort((a, b) => a.getTime() - b.getTime());
  const replaceRegex = new RegExp(Object.keys(ReplaceMap).join("|"), "gi");
  //make the string uppercase to limit the number of cases to check
  //trim the string to remove any leading or trailing whitespace
  //replace any non-alphanumeric delimiter with a space
  //replace any common date filter parts with the correct string
  //split the string into an array of strings
  const dateFilterParts = dateFilterString
    .toUpperCase()
    .trim()
    .replace(/[\W_]+/g, " ")
    .replace(replaceRegex, (match) => ReplaceMap[match])
    .split(" ");
  return getDateRangeFromDateFilterParts(dateFilterParts, fiscalMonthIndex, sortedDaysOff);
}
